import { useState } from 'react';
import type { Workspace } from '../types/schema';
import {
  organizationService,
  type OrganizationRole,
} from '../services/organization-service';
import { useUsers } from '../contexts/users-context';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Check, Loader2, UserPlus } from 'lucide-react';

interface InviteMembersModalProps {
  workspace: Workspace;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InviteMembersModal({
  workspace,
  open,
  onOpenChange,
}: InviteMembersModalProps) {
  const { refetchUsers } = useUsers();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<OrganizationRole>('MEMBER');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const reset = () => {
    setUsername('');
    setPassword('');
    setFirstName('');
    setLastName('');
    setEmail('');
    setRole('MEMBER');
    setError(null);
    setSuccess(false);
  };

  const handleClose = () => {
    reset();
    onOpenChange(false);
  };

  const handleCreate = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await organizationService.createMember({
        username: username.trim(),
        password,
        role,
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        email: email.trim() || undefined,
      });
      await refetchUsers();
      setSuccess(true);
      setTimeout(handleClose, 1200);
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : 'Failed to create account',
      );
    } finally {
      setIsLoading(false);
    }
  };

  const valid =
    username.trim().length >= 2 &&
    password.length >= 12 &&
    /[a-z]/.test(password) &&
    /[A-Z]/.test(password) &&
    /\d/.test(password) &&
    /[^a-zA-Z0-9]/.test(password);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-foreground" />
            Add organization member
          </DialogTitle>
          <DialogDescription>
            Create a login for your organization. The member can access{' '}
            <span className="font-medium text-foreground">{workspace.name}</span>{' '}
            and other organization workspaces.
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="py-8 text-center">
            <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-primary/10 text-primary mb-4">
              <Check className="h-6 w-6" />
            </div>
            <p className="text-foreground font-medium">Account created</p>
            <p className="text-sm text-muted-foreground mt-1">
              Share the organization slug, username, and password securely.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Input
                value={firstName}
                onChange={(event) => setFirstName(event.target.value)}
                placeholder="First name"
                disabled={isLoading}
              />
              <Input
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
                placeholder="Last name"
                disabled={isLoading}
              />
            </div>
            <Input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="name@company.com (optional)"
              disabled={isLoading}
            />
            <Input
              value={username}
              onChange={(event) => setUsername(event.target.value)}
              placeholder="Username"
              autoComplete="off"
              disabled={isLoading}
            />
            <Input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Temporary password"
              autoComplete="new-password"
              disabled={isLoading}
            />
            <p className="text-xs text-muted-foreground">
              Use 12+ characters with uppercase, lowercase, number, and symbol.
            </p>
            <select
              value={role}
              onChange={(event) =>
                setRole(event.target.value as OrganizationRole)
              }
              className="flex h-9 w-full rounded-component border border-border bg-background px-3 py-1 text-sm"
              disabled={isLoading}
            >
              <option value="MEMBER">Member</option>
              <option value="MANAGER">Manager</option>
              <option value="ADMIN">Admin</option>
            </select>
            {error && (
              <div className="text-sm text-danger bg-danger/10 px-3 py-2 rounded-component">
                {error}
              </div>
            )}
          </div>
        )}

        {!success && (
          <DialogFooter>
            <Button variant="outline" onClick={handleClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={isLoading || !valid}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create account
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
